import { create } from "zustand";
import { persist } from "zustand/middleware";

import type { GuideConfig, GuideStep } from "@/config/guides/types";

interface PendingNavigation {
  route: string;
  stepIndex: number;
}

interface GuideState {
  activeGuide: GuideConfig | null;
  currentStepIndex: number;
  isRunning: boolean;
  completedGuides: string[];
  pendingNavigation: PendingNavigation | null;
}

interface GuideActions {
  startGuide: (guide: GuideConfig) => void;
  nextStep: () => GuideStep | null;
  prevStep: () => GuideStep | null;
  goToStep: (index: number) => GuideStep | null;
  endGuide: () => void;
  markCompleted: (guideId: string) => void;
  hasCompleted: (guideId: string) => boolean;
  setPendingNavigation: (pending: PendingNavigation | null) => void;
  clearPendingNavigation: () => void;
  reset: () => void;
}

type GuideStore = GuideState & GuideActions;

const initialState: GuideState = {
  activeGuide: null,
  currentStepIndex: 0,
  isRunning: false,
  completedGuides: [],
  pendingNavigation: null,
};

export const useGuideStore = create<GuideStore>()(
  persist(
    (set, get) => ({
      ...initialState,

      startGuide: (guide: GuideConfig) => {
        set({
          activeGuide: guide,
          currentStepIndex: 0,
          isRunning: true,
          pendingNavigation: null,
        });
      },

      nextStep: () => {
        const { activeGuide, currentStepIndex } = get();
        if (!activeGuide) return null;

        const nextIndex = currentStepIndex + 1;
        if (nextIndex >= activeGuide.steps.length) {
          return null;
        }

        const nextStep = activeGuide.steps[nextIndex];
        if (nextStep?.route) {
          set({
            pendingNavigation: {
              route: nextStep.route,
              stepIndex: nextIndex,
            },
          });
        } else {
          set({ currentStepIndex: nextIndex });
        }

        return nextStep ?? null;
      },

      prevStep: () => {
        const { activeGuide, currentStepIndex } = get();
        if (!activeGuide) return null;

        const prevIndex = currentStepIndex - 1;
        if (prevIndex < 0) {
          return null;
        }

        const prevStep = activeGuide.steps[prevIndex];
        if (prevStep?.route) {
          set({
            pendingNavigation: {
              route: prevStep.route,
              stepIndex: prevIndex,
            },
          });
        } else {
          set({ currentStepIndex: prevIndex });
        }

        return prevStep ?? null;
      },

      goToStep: (index: number) => {
        const { activeGuide } = get();
        if (!activeGuide) return null;

        if (index < 0 || index >= activeGuide.steps.length) {
          return null;
        }

        const step = activeGuide.steps[index];
        if (step?.route) {
          set({
            pendingNavigation: {
              route: step.route,
              stepIndex: index,
            },
          });
        } else {
          set({ currentStepIndex: index });
        }

        return step ?? null;
      },

      endGuide: () => {
        const { activeGuide } = get();
        if (activeGuide) {
          get().markCompleted(activeGuide.id);
        }
        set({
          activeGuide: null,
          currentStepIndex: 0,
          isRunning: false,
          pendingNavigation: null,
        });
      },

      markCompleted: (guideId: string) => {
        set(state => {
          if (state.completedGuides.includes(guideId)) {
            return state;
          }
          return {
            completedGuides: [...state.completedGuides, guideId],
          };
        });
      },

      hasCompleted: (guideId: string) => {
        return get().completedGuides.includes(guideId);
      },

      setPendingNavigation: (pending: PendingNavigation | null) => {
        set({ pendingNavigation: pending });
      },

      clearPendingNavigation: () => {
        const { pendingNavigation } = get();
        if (pendingNavigation) {
          set({
            currentStepIndex: pendingNavigation.stepIndex,
            pendingNavigation: null,
          });
        }
      },

      reset: () => {
        set(initialState);
      },
    }),
    {
      name: "guide-storage",
      partialize: state => ({
        completedGuides: state.completedGuides,
      }),
    },
  ),
);
